import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, buildODataUrl, Toast, StandardDataGrid, getODataRows, resolveODataTotalCount } from './listPageStyles';

const API_BASE = '/api/customerdynamicgroups';

// Criteria types — must match backend CustomerDynamicGroupsController
const TYPE_GROUP = 1;
const TYPE_SUBGROUP = 2;
const TYPE_CODE = 3;

const TYPE_OPTIONS = [
  { value: TYPE_GROUP, label: 'Grup' },
  { value: TYPE_SUBGROUP, label: 'Ek Grup' },
  { value: TYPE_CODE, label: 'Kod' },
];

const gridActionButton = {
  border: 'none',
  borderRadius: 6,
  color: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  height: 30,
  lineHeight: '30px',
  padding: '0 10px',
  minWidth: 74,
};

function unwrapLookupItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const desc = item.description || item.Description || item.name || item.Name || item.title || item.Title || '';
  return desc ? `${code} - ${desc}` : code;
}

const emptyHeader = { id: '', textId: '', description: '' };

export function CustomerDynamicGroupsPageLegacy() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyHeader);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  // criteria (detail rows) being edited in the modal
  const [criteria, setCriteria] = useState([]);
  const [deletedDetailIds, setDeletedDetailIds] = useState([]);

  // criteria builder pending state
  const [criteriaType, setCriteriaType] = useState(TYPE_GROUP);
  const [pendingGroupId, setPendingGroupId] = useState('');
  const [pendingSubGroupId, setPendingSubGroupId] = useState('');
  const [pendingCustomerId, setPendingCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  // preview pool
  const [previewRows, setPreviewRows] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // lookups
  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);

  const [sortField, setSortField] = useState('TextId');
  const [sortDir, setSortDir] = useState('asc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let filterParams = '';
      if (filterText.trim()) {
        const q = filterText.trim().replace(/'/g, "''").toLowerCase();
        filterParams = `(contains(tolower(TextId),'${q}') or contains(tolower(Description),'${q}'))`;
      }

      const url = buildODataUrl(API_BASE, { pageSize, currentPage, sortField, sortDir, filterParams });
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alinamadi');

      const data = await res.json();
      const pageRows = getODataRows(data);
      setRows(pageRows);
      setTotalCount(await resolveODataTotalCount({
        data, rows: pageRows, currentPage, pageSize, base: API_BASE, filterParams, headers: authHeaders(),
      }));
    } catch (error) {
      showToast('error', error.message || 'Veri alinirken hata olustu');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterText, pageSize, sortDir, sortField]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // load customer group / sub-group lookups once
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/customergroups/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/customersubgroups/Select', { headers }).then(res => res.ok ? res.json() : []),
    ]).then(([groupData, subGroupData]) => {
      setGroups(unwrapLookupItems(groupData));
      setSubGroups(unwrapLookupItems(subGroupData));
    }).catch(() => {});
  }, []);

  const handleFilterSearch = () => { setFilterText(filterInput); setCurrentPage(1); };
  const handleFilterKeyDown = (event) => { if (event.key === 'Enter') handleFilterSearch(); };
  const clearFilter = () => { setFilterText(''); setFilterInput(''); setCurrentPage(1); };

  const resetCriteriaBuilder = () => {
    setCriteriaType(TYPE_GROUP);
    setPendingGroupId('');
    setPendingSubGroupId('');
    setPendingCustomerId('');
    setCustomerSearch('');
    setCustomerResults([]);
    setPreviewRows(null);
  };

  const openNew = () => {
    setForm(emptyHeader);
    setCriteria([]);
    setDeletedDetailIds([]);
    resetCriteriaBuilder();
    setSelected(null);
    setModal('new');
  };

  const loadDetails = useCallback(async (row, mode) => {
    if (!row) return;
    setSelected(row);
    setForm({ id: row.id, textId: row.textId || '', description: row.description || '' });
    setCriteria([]);
    setDeletedDetailIds([]);
    resetCriteriaBuilder();
    setModal(mode);
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${row.id}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Detay alinamadi');
      const details = (body?.data?.details || body?.Data?.details || []).map(d => ({
        id: d.id || d.Id,
        criteriaType: d.criteriaType ?? d.CriteriaType,
        groupId: d.groupId ?? d.GroupId ?? null,
        subGroupId: d.subGroupId ?? d.SubGroupId ?? null,
        customerId: d.customerId ?? d.CustomerId ?? null,
        label: d.label ?? d.Label ?? '',
      }));
      setCriteria(details);
    } catch (error) {
      showToast('error', error.message || 'Detay alinirken hata olustu');
    }
  }, []);

  const openEdit = useCallback((row = selected) => loadDetails(row, 'edit'), [selected, loadDetails]);
  const openView = useCallback((row = selected) => loadDetails(row, 'view'), [selected, loadDetails]);

  const closeModal = () => { setModal(null); setPreviewRows(null); };

  const searchCustomers = async () => {
    const q = customerSearch.trim().replace(/'/g, "''").toLowerCase();
    if (!q) { setCustomerResults([]); return; }
    setSearchingCustomers(true);
    try {
      const filter = `(contains(tolower(TextId),'${q}') or contains(tolower(Title),'${q}'))`;
      const url = `/api/customers?$top=20&$orderby=TextId&$filter=${encodeURIComponent(filter)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Musteri aramasi basarisiz');
      const data = await res.json();
      setCustomerResults(getODataRows(data));
    } catch (error) {
      showToast('error', error.message || 'Musteri aranirken hata olustu');
    } finally {
      setSearchingCustomers(false);
    }
  };

  const addCriterion = () => {
    let criterion = null;
    if (criteriaType === TYPE_GROUP) {
      if (!pendingGroupId) { showToast('error', 'Bir grup seciniz'); return; }
      const g = groups.find(item => item.id === pendingGroupId);
      criterion = { id: '', criteriaType: TYPE_GROUP, groupId: pendingGroupId, subGroupId: null, customerId: null, label: `Grup: ${lookupLabel(g)}` };
    } else if (criteriaType === TYPE_SUBGROUP) {
      if (!pendingSubGroupId) { showToast('error', 'Bir ek grup seciniz'); return; }
      const g = subGroups.find(item => item.id === pendingSubGroupId);
      criterion = { id: '', criteriaType: TYPE_SUBGROUP, groupId: null, subGroupId: pendingSubGroupId, customerId: null, label: `Ek Grup: ${lookupLabel(g)}` };
    } else {
      if (!pendingCustomerId) { showToast('error', 'Bir musteri seciniz'); return; }
      const c = customerResults.find(item => item.id === pendingCustomerId);
      criterion = { id: '', criteriaType: TYPE_CODE, groupId: null, subGroupId: null, customerId: pendingCustomerId, label: `Kod: ${lookupLabel(c)}` };
    }

    // avoid duplicate criteria
    const exists = criteria.some(c =>
      c.criteriaType === criterion.criteriaType &&
      c.groupId === criterion.groupId &&
      c.subGroupId === criterion.subGroupId &&
      c.customerId === criterion.customerId);
    if (exists) { showToast('error', 'Bu kriter zaten ekli'); return; }

    setCriteria(current => [...current, criterion]);
    setPendingGroupId('');
    setPendingSubGroupId('');
    setPendingCustomerId('');
    setCustomerSearch('');
    setCustomerResults([]);
    setPreviewRows(null);
  };

  const removeCriterion = (index) => {
    setCriteria(current => {
      const target = current[index];
      if (target?.id) setDeletedDetailIds(ids => ids.includes(target.id) ? ids : [...ids, target.id]);
      return current.filter((_, i) => i !== index);
    });
    setPreviewRows(null);
  };

  const buildDetailPayload = () => criteria.map(c => ({
    id: c.id || '00000000-0000-0000-0000-000000000000',
    criteriaType: c.criteriaType,
    groupId: c.groupId,
    subGroupId: c.subGroupId,
    customerId: c.customerId,
  }));

  const previewPool = async () => {
    if (criteria.length === 0) { showToast('error', 'Once kriter ekleyin'); return; }
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API_BASE}/ResolveCustomers`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ details: buildDetailPayload() }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Onizleme basarisiz');
      const pool = body?.data ?? body?.Data ?? [];
      setPreviewRows(Array.isArray(pool) ? pool : []);
    } catch (error) {
      showToast('error', error.message || 'Onizleme sirasinda hata olustu');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.textId.trim()) { showToast('error', 'Kod bos olamaz'); return; }
    try {
      const res = await fetch(`${API_BASE}/SaveWithDetails`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          header: { id: form.id || '00000000-0000-0000-0000-000000000000', textId: form.textId, description: form.description },
          details: buildDetailPayload(),
          deletedDetailIds,
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayit kaydedilemedi');
      showToast('success', 'Ozellestirilmis grup basariyla kaydedildi');
      closeModal();
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Islem sirasinda hata olustu');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ id: selected.id }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayit silinemedi');
      showToast('success', 'Ozellestirilmis grup basariyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme islemi sirasinda hata olustu');
      setDeleteConfirm(false);
    }
  };

  const columns = useMemo(() => [
    { field: 'textId', headerName: 'Kod', flex: 1, minWidth: 150, sortable: true },
    { field: 'description', headerName: 'Aciklama', flex: 1.6, minWidth: 220, sortable: true },
    {
      field: 'createdDate', headerName: 'Olusturma Tarihi', width: 170, sortable: true,
      renderCell: (params) => params.row.createdDate ? new Date(params.row.createdDate).toLocaleDateString('tr-TR') : '-',
    },
    {
      field: 'updatedDate', headerName: 'Guncelleme Tarihi', width: 180, sortable: true,
      renderCell: (params) => params.row.updatedDate ? new Date(params.row.updatedDate).toLocaleDateString('tr-TR') : '-',
    },
    {
      field: 'actions', headerName: 'Islemler', width: 260, sortable: false, filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}>
          <button style={{ ...gridActionButton, background: 'var(--accent)' }} onClick={(event) => { event.stopPropagation(); openView(params.row); }}>Goruntule</button>
          <button style={{ ...gridActionButton, background: 'var(--warn)' }} onClick={(event) => { event.stopPropagation(); openEdit(params.row); }}>Degistir</button>
          <button
            style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48 }}
            onClick={(event) => { event.stopPropagation(); setSelected(params.row); setDeleteConfirm(true); }}
          >
            Sil
          </button>
        </div>
      ),
    },
  ], [openEdit, openView]);

  const isView = modal === 'view';

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>Müşteri Özelleştirilmiş Grup</h2>
          <p style={s.pageSubtitle}>Grup, ek grup veya kod bazında dinamik müşteri havuzları tanımlayın.</p>
        </div>
        <button style={{ ...s.btnPrimary, background: 'var(--success)' }} onClick={openNew}>+ Yeni Özelleştirilmiş Grup</button>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Kod veya açıklama ile filtrele..."
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={handleFilterKeyDown}
          />
          {filterText && <button style={s.btnSecondary} onClick={clearFilter}>Temizle</button>}
          <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
        </div>
      </div>

      <StandardDataGrid
        rows={rows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={page => setCurrentPage(page)}
        onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, direction) => { setCurrentPage(1); setSortField(field || 'TextId'); setSortDir(direction || 'asc'); }}
        selectedId={selected?.id}
        onRowClick={row => setSelected(row)}
        onRowDoubleClick={row => openView(row)}
        exportFileName="musteri-ozellestirilmis-gruplar"
      />

      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={{ ...s.modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {modal === 'new' ? 'Yeni Özelleştirilmiş Grup' : modal === 'edit' ? 'Özelleştirilmiş Grup Düzenle' : 'Özelleştirilmiş Grup Detay'}
              </h3>
              <button style={s.closeBtn} onClick={closeModal}>x</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kod *</label>
                  <input
                    style={isView ? s.inputView : s.input}
                    value={form.textId}
                    onChange={e => setForm(current => ({ ...current, textId: e.target.value }))}
                    readOnly={isView}
                    placeholder="Kod giriniz"
                    autoFocus
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Açıklama</label>
                  <input
                    style={isView ? s.inputView : s.input}
                    value={form.description}
                    onChange={e => setForm(current => ({ ...current, description: e.target.value }))}
                    readOnly={isView}
                    placeholder="Açıklama giriniz"
                  />
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--border-color, #e5e7eb)', paddingTop: 14 }}>
                <label style={{ ...s.label, fontSize: 14, marginBottom: 8, display: 'block' }}>Müşteri Havuzu Kuralları</label>

                {!isView && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
                    <div style={{ ...s.formGroup, minWidth: 130 }}>
                      <label style={s.label}>Tür</label>
                      <select
                        style={s.select}
                        value={criteriaType}
                        onChange={e => { setCriteriaType(Number(e.target.value)); setPendingGroupId(''); setPendingSubGroupId(''); setPendingCustomerId(''); setCustomerResults([]); setCustomerSearch(''); }}
                      >
                        {TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>

                    {criteriaType === TYPE_GROUP && (
                      <div style={{ ...s.formGroup, flex: 1, minWidth: 220 }}>
                        <label style={s.label}>Müşteri Grubu</label>
                        <select style={s.select} value={pendingGroupId} onChange={e => setPendingGroupId(e.target.value)}>
                          <option value="">Seçiniz</option>
                          {groups.map(g => <option key={g.id} value={g.id}>{lookupLabel(g)}</option>)}
                        </select>
                      </div>
                    )}

                    {criteriaType === TYPE_SUBGROUP && (
                      <div style={{ ...s.formGroup, flex: 1, minWidth: 220 }}>
                        <label style={s.label}>Müşteri Ek Grubu</label>
                        <select style={s.select} value={pendingSubGroupId} onChange={e => setPendingSubGroupId(e.target.value)}>
                          <option value="">Seçiniz</option>
                          {subGroups.map(g => <option key={g.id} value={g.id}>{lookupLabel(g)}</option>)}
                        </select>
                      </div>
                    )}

                    {criteriaType === TYPE_CODE && (
                      <div style={{ ...s.formGroup, flex: 1, minWidth: 260 }}>
                        <label style={s.label}>Müşteri (kod/ad ara)</label>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input
                            style={{ ...s.input, flex: 1 }}
                            value={customerSearch}
                            onChange={e => setCustomerSearch(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchCustomers(); } }}
                            placeholder="Müşteri kodu veya adı"
                          />
                          <button style={s.btnSecondary} onClick={searchCustomers} disabled={searchingCustomers}>{searchingCustomers ? '...' : 'Ara'}</button>
                        </div>
                        {customerResults.length > 0 && (
                          <select style={{ ...s.select, marginTop: 6 }} value={pendingCustomerId} onChange={e => setPendingCustomerId(e.target.value)}>
                            <option value="">Sonuçlardan seçiniz</option>
                            {customerResults.map(c => <option key={c.id} value={c.id}>{lookupLabel(c)}</option>)}
                          </select>
                        )}
                      </div>
                    )}

                    <button style={{ ...s.btnPrimary, height: 38 }} onClick={addCriterion}>Ekle</button>
                  </div>
                )}

                {criteria.length === 0 ? (
                  <p style={{ margin: '4px 0', color: 'var(--text-secondary, #6b7280)', fontSize: 13 }}>Henüz kriter eklenmedi.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {criteria.map((c, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 10, background: 'var(--bg-card, #f9fafb)' }}>
                        <span style={{ fontSize: 13, color: 'var(--text-primary, #1f2937)' }}>{c.label || '(kriter)'}</span>
                        {!isView && (
                          <button style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48, height: 26, lineHeight: '26px' }} onClick={() => removeCriterion(index)}>Sil</button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
                  <button style={s.btnSecondary} onClick={previewPool} disabled={previewLoading}>{previewLoading ? 'Hesaplanıyor...' : 'Havuzu Önizle'}</button>
                  {Array.isArray(previewRows) && (
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary, #374151)' }}>{previewRows.length} müşteri</span>
                  )}
                </div>

                {Array.isArray(previewRows) && previewRows.length > 0 && (
                  <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 10 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>Kod</th>
                          <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>Ünvan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.map(c => (
                          <tr key={c.id || c.Id}>
                            <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-color, #f1f5f9)' }}>{c.textId ?? c.TextId}</td>
                            <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--border-color, #f1f5f9)' }}>{c.title ?? c.Title ?? c.shortName ?? c.ShortName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div style={s.modalFooter}>
              {!isView && <button style={s.btnPrimary} onClick={handleSave}>Kaydet</button>}
              <button style={s.btnSecondary} onClick={closeModal}>{isView ? 'Kapat' : 'İptal'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>"{selected?.textId}"</strong> kodlu özelleştirilmiş grubu silmek istediğinize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
